import { useCallback, useEffect, useRef, useState } from 'react'
import { create } from 'zustand'

/**
 * Spoken prompts (Brian, SCREEN-INVENTORY #5): the guide reads each question
 * aloud with the browser's speech synthesis, with a mute that is remembered.
 * Answers are typed, or dictated where the browser has speech recognition.
 */

const MUTE_KEY = 'gg.onboarding.muted'

function readMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1' } catch { return false }
}

export const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window

function pickVoice(): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices().filter((v) => v.lang?.toLowerCase().startsWith('en'))
  // A natural sounding English voice where the system has one.
  return voices.find((v) => /samantha|ava|allison|google us english|jenny|aria/i.test(v.name))
    ?? voices.find((v) => v.default) ?? voices[0] ?? null
}

/** Shared, so the page's mute and the interview's mute are one switch. */
const useMuted = create<{ muted: boolean; set: (m: boolean) => void }>((set) => ({
  muted: readMuted(),
  set: (muted) => set({ muted }),
}))

export function useSpokenPrompts() {
  const muted = useMuted((s) => s.muted)
  const setMutedState = useMuted((s) => s.set)

  const stop = useCallback(() => { if (canSpeak) window.speechSynthesis.cancel() }, [])

  const speak = useCallback((text: string) => {
    if (!canSpeak || muted || !text) return
    const synth = window.speechSynthesis
    synth.cancel()
    const u = new SpeechSynthesisUtterance(text)
    const v = pickVoice()
    if (v) u.voice = v
    u.rate = 1
    u.pitch = 1
    synth.speak(u)
  }, [muted])

  const setMuted = useCallback((m: boolean) => {
    setMutedState(m)
    try { localStorage.setItem(MUTE_KEY, m ? '1' : '0') } catch { /* private window */ }
    if (m && canSpeak) window.speechSynthesis.cancel()
  }, [setMutedState])

  // Voices load late in Chrome; touching the list starts the load.
  useEffect(() => { if (canSpeak) window.speechSynthesis.getVoices() }, [])
  useEffect(() => () => stop(), [stop])

  return { speak, stop, muted, setMuted, available: canSpeak }
}

/* ---- Dictation (Web Speech API, Chrome and Safari) ---- */

interface RecognitionResultEvent {
  resultIndex: number
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>
}
interface Recognition {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((e: RecognitionResultEvent) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
  start(): void
  stop(): void
}
type RecognitionCtor = new () => Recognition

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export const canDictate = !!recognitionCtor()

/** Dictation: each final phrase is handed to onText. */
export function useDictation(onText: (text: string) => void, lang = 'en-US') {
  const [listening, setListening] = useState(false)
  const rec = useRef<Recognition | null>(null)
  const cb = useRef(onText)
  cb.current = onText

  const stop = useCallback(() => { rec.current?.stop() }, [])

  const start = useCallback(() => {
    const Ctor = recognitionCtor()
    if (!Ctor || rec.current) return
    if (canSpeak) window.speechSynthesis.cancel()
    const r = new Ctor()
    r.lang = lang
    r.continuous = true
    r.interimResults = false
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]
        if (res.isFinal) cb.current(res[0].transcript.trim())
      }
    }
    r.onend = () => { rec.current = null; setListening(false) }
    r.onerror = () => { rec.current = null; setListening(false) }
    rec.current = r
    try { r.start(); setListening(true) } catch { rec.current = null }
  }, [lang])

  useEffect(() => () => rec.current?.stop(), [])

  return { listening, start, stop, available: canDictate }
}
